import type {
  Attempt,
  FactId,
  StrategyEvidence,
  StrategyId,
  SchedulingState,
} from "@oefenheld/reken-core";
import type {
  AchievementUnlock,
  ItemPresentation,
  LegacyImportRecord,
  LearnerProfile,
  LearningCommit,
  SelectorDecisionRecord,
  SessionRecord,
  StoredFactMastery,
  StoredSkillMastery,
  StoredStrategyMastery,
  StrategyLearningCommit,
  SchedulingCommit,
} from "./schema.ts";

export interface LearningRepository {
  withProfileLock<T>(profileId: string, action: () => Promise<T>): Promise<T>;
  getScheduling(profileId: string): Promise<SchedulingState | null>;
  commitScheduling(commit: SchedulingCommit): Promise<void>;
  commitProgress(presentation: ItemPresentation, scheduling: SchedulingCommit): Promise<void>;
  listPresentations(profileId: string): Promise<readonly ItemPresentation[]>;
  reconcilePresentations(profileId: string, at: string): Promise<void>;
  getProfile(profileId: string): Promise<LearnerProfile | null>;
  putProfile(profile: LearnerProfile): Promise<void>;

  getSession(sessionId: string): Promise<SessionRecord | null>;
  putSession(session: SessionRecord): Promise<void>;
  closeSession(
    session: SessionRecord,
    abandonedPresentations: readonly ItemPresentation[],
  ): Promise<void>;
  listSessionsForProfile(profileId: string): Promise<readonly SessionRecord[]>;

  putSelectorDecision(decision: SelectorDecisionRecord): Promise<void>;
  getSelectorDecision(decisionId: string): Promise<SelectorDecisionRecord | null>;
  putPresentation(presentation: ItemPresentation): Promise<void>;
  commitPresentation(
    decision: SelectorDecisionRecord,
    presentation: ItemPresentation,
    scheduling?: SchedulingCommit,
  ): Promise<void>;
  getPresentation(presentationId: string): Promise<ItemPresentation | null>;
  listOpenPresentationsForSession(sessionId: string): Promise<readonly ItemPresentation[]>;

  listAttempts(profileId: string): Promise<readonly Attempt[]>;
  listAttemptsForFact(profileId: string, factId: FactId): Promise<readonly Attempt[]>;
  listAttemptsForSession(sessionId: string): Promise<readonly Attempt[]>;
  listStrategyEvidence(
    profileId: string,
    strategyId: StrategyId,
  ): Promise<readonly StrategyEvidence[]>;

  getFactMastery(
    profileId: string,
    factId: FactId,
  ): Promise<StoredFactMastery | null>;
  listFactMastery(profileId: string): Promise<readonly StoredFactMastery[]>;
  getStrategyMastery(
    profileId: string,
    strategyId: StrategyId,
  ): Promise<StoredStrategyMastery | null>;
  listStrategyMastery(profileId: string): Promise<readonly StoredStrategyMastery[]>;
  getSkillMastery(
    profileId: string,
    skillId: string,
  ): Promise<StoredSkillMastery | null>;
  listSkillMastery(profileId: string): Promise<readonly StoredSkillMastery[]>;
  listAchievementUnlocks(profileId: string): Promise<readonly AchievementUnlock[]>;

  putLegacyImport(record: LegacyImportRecord): Promise<void>;
  listLegacyImports(profileId: string): Promise<readonly LegacyImportRecord[]>;

  commitLearningResult(commit: LearningCommit): Promise<void>;
  commitStrategyLearning(commit: StrategyLearningCommit): Promise<void>;
}

export function validateSchedulingCommit(current: SchedulingState | null, commit: SchedulingCommit, profileId: string): void {
  if (commit.state.profileId !== profileId || (current?.revision ?? 0) !== commit.expectedRevision
    || commit.state.revision !== commit.expectedRevision + 1) {
    throw new Error("Schedulingconflict: herlaad de actuele leeractiviteit.");
  }
}

export function factMasteryKey(profileId: string, factId: FactId): string {
  return `${profileId}::${factId}`;
}

export function strategyMasteryKey(
  profileId: string,
  strategyId: StrategyId,
): string {
  return `${profileId}::${strategyId}`;
}

export function skillMasteryKey(profileId: string, skillId: string): string {
  return `${profileId}::${skillId}`;
}

export function validateLearningCommit(commit: LearningCommit): void {
  if (commit.attempt.profileId !== commit.factMastery.profileId) {
    throw new Error("Attempt en fact mastery horen niet bij hetzelfde profiel.");
  }
  if (commit.attempt.factId !== commit.factMastery.factId) {
    throw new Error("Attempt en fact mastery horen niet bij dezelfde fact.");
  }
  if (commit.attempt.sessionId !== commit.session.id) {
    throw new Error("Attempt en session horen niet bij elkaar.");
  }
  if (
    commit.presentation.id !== commit.attempt.presentationId
    || commit.presentation.state !== "RESOLVED"
  ) {
    throw new Error("Learning commit vereist de opgeloste itempresentatie van de attempt.");
  }
  if (commit.attempt.profileId !== commit.session.profileId) {
    throw new Error("Attempt en session horen niet bij hetzelfde profiel.");
  }
  for (const evidence of commit.strategyEvidence) {
    if (
      evidence.profileId !== commit.attempt.profileId
      || evidence.sessionId !== commit.session.id
    ) {
      throw new Error("Strategy evidence hoort niet bij dezelfde leerling en sessie.");
    }
  }
  for (const mastery of [
    ...commit.strategyMastery,
    ...commit.skillMastery,
  ]) {
    if (mastery.profileId !== commit.attempt.profileId) {
      throw new Error("Masteryprojectie hoort niet bij hetzelfde profiel.");
    }
  }
  for (const unlock of commit.achievementUnlocks) {
    if (unlock.profileId !== commit.attempt.profileId) {
      throw new Error("Achievement unlock hoort niet bij hetzelfde profiel.");
    }
  }
}

export function validateStrategyLearningCommit(commit: StrategyLearningCommit): void {
  if (commit.presentation.state !== "RESOLVED") {
    throw new Error("Strategy learning commit vereist een opgeloste itempresentatie.");
  }
  if (
    commit.presentation.sessionId !== commit.session.id
    || commit.presentation.profileId !== commit.session.profileId
  ) {
    throw new Error("Strategy presentation en session horen niet bij elkaar.");
  }
  if (commit.presentation.strategyId !== commit.strategyMastery.strategyId) {
    throw new Error("Strategy presentation en mastery horen niet bij dezelfde strategie.");
  }
  if (commit.strategyMastery.profileId !== commit.session.profileId) {
    throw new Error("Strategy mastery hoort niet bij hetzelfde profiel.");
  }
  for (const evidence of commit.strategyEvidence) {
    if (
      evidence.profileId !== commit.session.profileId
      || evidence.sessionId !== commit.session.id
      || evidence.strategyId !== commit.strategyMastery.strategyId
    ) {
      throw new Error("Strategy evidence hoort niet bij dezelfde leerling, sessie en strategie.");
    }
  }
  for (const mastery of commit.skillMastery) {
    if (mastery.profileId !== commit.session.profileId) {
      throw new Error("Skill mastery hoort niet bij hetzelfde profiel.");
    }
  }
  for (const unlock of commit.achievementUnlocks) {
    if (unlock.profileId !== commit.session.profileId) {
      throw new Error("Achievement unlock hoort niet bij hetzelfde profiel.");
    }
  }
}
